import { clsx } from 'clsx';
import { Heading, Heart3, Paragraph } from '@hungryroot/ui-kit';
import styles from './AddToCookbookToast.module.css';

/**
 * The wording used when no cookbook is named. Exp 242 control still shows this:
 * it has a single cookbook, so naming it would add nothing.
 */
export const ADD_TO_COOKBOOK_TOAST_COPY = {
  added: 'Added to my cookbook',
  removed: 'Removed from my cookbook',
} as const;

/**
 * The toast's single trailing action.
 *
 * A union rather than two optional callbacks because the design gives every
 * case exactly one: an add offers **Manage** (route this somewhere else), a
 * removal offers **Undo**. Both at once is not a state that exists.
 */
export type AddToCookbookToastAction =
  | { type: 'undo'; onUndo: () => void }
  | { type: 'manage'; onManage: () => void };

interface AddToCookbookToastProps {
  isInCookbook: boolean;
  /**
   * Names the cookbook in the copy — "Added to Weeknight Dinners". Omit for the
   * generic wording above.
   */
  cookbookName?: string;
  /**
   * Replaces the generated sentence. Used when saving a whole cookbook, which
   * is not "added to" a named cookbook.
   */
  copy?: string;
  action: AddToCookbookToastAction;
  onDismiss: () => void;
}

function toastCopy(
  isInCookbook: boolean,
  cookbookName?: string,
  copy?: string
): string {
  if (copy != null) {
    return copy;
  }

  if (cookbookName == null) {
    return isInCookbook
      ? ADD_TO_COOKBOOK_TOAST_COPY.added
      : ADD_TO_COOKBOOK_TOAST_COPY.removed;
  }

  return isInCookbook
    ? `Added to ${cookbookName}`
    : `Removed from ${cookbookName}`;
}

export function AddToCookbookToast(props: AddToCookbookToastProps) {
  const { isInCookbook, cookbookName, copy, action, onDismiss } = props;

  return (
    <div className={styles.toast} onClick={onDismiss}>
      <Heart3
        className={clsx(styles.heart, {
          [styles.favorited]: isInCookbook,
          [styles.removed]: !isInCookbook,
        })}
      />
      <Paragraph variant="body" className={styles.copy}>
        {toastCopy(isInCookbook, cookbookName, copy)}
      </Paragraph>
      <button
        type="button"
        className={styles.action}
        onClick={(e) => {
          // The toast itself dismisses on tap, so without this the action would
          // also trigger that handler.
          e.stopPropagation();
          if (action.type === 'undo') {
            action.onUndo();
          } else {
            action.onManage();
          }
          onDismiss();
        }}
      >
        <Heading variant="H7">
          {action.type === 'undo' ? 'Undo' : 'Manage'}
        </Heading>
      </button>
    </div>
  );
}
